// src/pages/purchase-requisition/hooks/useOrderForm.jsx
import { useState, useCallback } from "react";
import { submitNewOrder, resubmitOrder } from "../services/orderService";
import { generateShortId } from "../../../utils/helpers";

/**
 * Custom hook to manage the order form state and logic, including material item details.
 * @param {object} user - The authenticated user object.
 * @param {object} userProfile - The user's profile data.
 * @param {Array} flowConfigurations - List of flow configurations.
 * @param {function} setSnackbar - Function to display snackbar messages.
 * @returns {object} An object containing form states and handlers.
 */
const useOrderForm = (user, userProfile, flowConfigurations, setSnackbar) => {
    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [formValues, setFormValues] = useState({
        flow_config_id: '',
        item_details: [],
        order_title: '',
        total_amount: 0,
    });
    const [openItemDetailDialog, setOpenItemDetailDialog] = useState(false);
    const [currentItemDetail, setCurrentItemDetail] = useState(null);
    const [currentItemDetailIndex, setCurrentItemDetailIndex] = useState(null);
    const [orderBeingEditedId, setOrderBeingEditedId] = useState(null);

const handleOpenFormDialog = useCallback((orderData = null) => {
    if (orderData) {
        setFormValues({
            flow_config_id: orderData.flow_config_id,
            item_details: orderData.item_details || [],
            order_title: orderData.order_title || '',
            total_amount: orderData.total_amount || 0,
            // TAMBAHKAN: Simpan riwayat persetujuan yang sudah ada
            approval_history: orderData.approval_history || []
        });
        setOrderBeingEditedId(orderData.id);
    } else {
        setFormValues({
            flow_config_id: userProfile?.id_flow_order_default || '',
            item_details: [],
            order_title: '',
            total_amount: 0,
            approval_history: [] // TAMBAHKAN untuk new order
        });
        setOrderBeingEditedId(null);
    }
    setOpenFormDialog(true);
}, [userProfile]);

    const handleCloseFormDialog = useCallback(() => {
        setOpenFormDialog(false);
        setOrderBeingEditedId(null);
        setFormValues({
            flow_config_id: '',
            item_details: [],
        });
    }, []);

    const handleOpenItemDetailDialog = useCallback((item = null, index = null) => {
        setCurrentItemDetail(item ? { ...item } : {
            material_code: '',
            material_description: '',
            quantity_jul: '',
            unit_of_measure: '',
            valuation_price: '',
            total_amount: '',
            cost_center: '',
            notes: '',
        });
        setCurrentItemDetailIndex(index);
        setOpenItemDetailDialog(true);
    }, []);

    const handleCloseItemDetailDialog = useCallback(() => {
        setOpenItemDetailDialog(false);
        setCurrentItemDetail(null);
        setCurrentItemDetailIndex(null);
    }, []);

    const handleItemDetailChange = useCallback((e) => {
        const { id, name, value } = e.target;
        setCurrentItemDetail(prev => {
            let newState = { ...prev };

            if (id === 'quantity_jul') {
                newState.quantity_jul = value;
            } else if (id === 'valuation_price') {
                const cleanedValue = value.replace(/[^0-9]/g, '');
                newState.valuation_price = cleanedValue;
            } else if (name === 'unit_of_measure') {
                newState.unit_of_measure = value;
            } else {
                newState[id] = value;
            }

            const qty = parseFloat(newState.quantity_jul) || 0;
            const price = parseFloat(String(newState.valuation_price).replace(/[^0-9]/g, '')) || 0;

            newState.total_amount = (qty * price);

            return newState;
        });
    }, []);

    const handleSaveItemDetail = useCallback(() => {
        if (!currentItemDetail.material_code || !currentItemDetail.material_description || !currentItemDetail.quantity_jul || !currentItemDetail.unit_of_measure) {
            setSnackbar({ open: true, message: "Harap isi semua kolom wajib untuk item material (Kode Material, Deskripsi, QTY, UoM).", severity: "warning" });
            return;
        }

        const updatedItemDetails = [...formValues.item_details];
        if (currentItemDetailIndex !== null) {
            updatedItemDetails[currentItemDetailIndex] = currentItemDetail;
        } else {
            updatedItemDetails.push({ id: generateShortId(), ...currentItemDetail });
        }
        setFormValues(prev => ({ ...prev, item_details: updatedItemDetails }));
        handleCloseItemDetailDialog();
    }, [currentItemDetail, currentItemDetailIndex, formValues.item_details, setSnackbar, handleCloseItemDetailDialog]);

    const handleDeleteItemDetail = useCallback((indexToDelete) => {
        setFormValues(prev => ({
            ...prev,
            item_details: prev.item_details.filter((_, index) => index !== indexToDelete)
        }));
    }, []);

const handleSubmitOrder = useCallback(async () => {
    if (!user || !userProfile || !userProfile.id_jabatan || !userProfile.id_role) {
        setSnackbar({ open: true, message: "Data profil pengguna Anda tidak ditemukan atau belum lengkap (jabatan/role). Harap lengkapi profil Anda atau hubungi administrator.", severity: "error" });
        return;
    }

    if (!formValues.flow_config_id) {
        setSnackbar({ open: true, message: "Pilih Alur Persetujuan terlebih dahulu.", severity: "warning" });
        return;
    }

    if (formValues.item_details.length === 0) {
        setSnackbar({ open: true, message: "Harap tambahkan setidaknya satu item material.", severity: "warning" });
        return;
    }

    const selectedFlow = flowConfigurations.find(flow => flow.id === formValues.flow_config_id);
    if (!selectedFlow || !selectedFlow.approval_steps || Object.keys(selectedFlow.approval_steps).length === 0) {
        setSnackbar({ open: true, message: "Alur persetujuan yang dipilih tidak memiliki langkah-langkah yang valid.", severity: "error" });
        return;
    }

    try {
        if (orderBeingEditedId) {
            // TAMBAHKAN: Buat entri riwayat untuk pengajuan ulang
            const resubmissionHistoryEntry = {
                step_name: "Pengajuan Ulang",
                approver_uid: user.uid,
                approver_name: userProfile.nama || user.email,
                approver_jabatan_id: userProfile.id_jabatan,
                approver_role_id: userProfile.id_role,
                timestamp: new Date().toISOString(),
                decision: "Resubmitted",
                notes: "Order diajukan ulang"
            };

            const updatedData = {
                flow_config_id: formValues.flow_config_id,
                item_details: formValues.item_details,
                order_title: formValues.order_title,
                total_amount: formValues.total_amount,
                status: "Pending Approval",
                current_approval_step_index: 0, // Reset ke step awal
                updated_at: new Date().toISOString(),
                // TAMBAHKAN: Riwayat persetujuan yang sudah ada ditambah entri pengajuan ulang
                approval_history: [
                    ...(Array.isArray(formValues.approval_history) ? formValues.approval_history : []),
                    resubmissionHistoryEntry
                ]
            };
            await resubmitOrder(orderBeingEditedId, updatedData);
            setSnackbar({ open: true, message: "Order berhasil diajukan ulang!", severity: "success" });
        } else {
            const newOrder = {
                flow_config_id: formValues.flow_config_id,
                item_details: formValues.item_details,
                order_title: formValues.order_title,
                total_amount: formValues.total_amount,
                created_at: new Date().toISOString(),
                created_by_uid: user.uid,
                created_by_name: userProfile.nama || user.email,
                created_by_jabatan_id: userProfile.id_jabatan,
                created_by_role_id: userProfile.id_role,
                current_approval_step_index: 0,
                status: "Pending Approval",
                approval_history: [],
            };
            await submitNewOrder(newOrder);
            setSnackbar({ open: true, message: "Permintaan order berhasil diajukan!", severity: "success" });
        }
        handleCloseFormDialog();
    } catch (error) {
        console.error("Error submitting order:", error);
        setSnackbar({ open: true, message: "Gagal mengajukan order: " + error.message, severity: "error" });
    }
}, [user, userProfile, formValues, flowConfigurations, orderBeingEditedId, setSnackbar, handleCloseFormDialog]);

    const handleResubmitOrder = useCallback((orderToResubmit) => {
        if (!user || !userProfile) {
            setSnackbar({ open: true, message: "Anda harus login untuk melakukan aksi ini.", severity: "error" });
            return;
        }

        if (orderToResubmit.created_by_uid !== user.uid) {
            setSnackbar({ open: true, message: "Anda hanya dapat mengajukan kembali order yang Anda buat.", severity: "error" });
            return;
        }

        if (orderToResubmit.status !== "Rejected") {
            setSnackbar({ open: true, message: "Order hanya dapat diajukan kembali jika statusnya 'Rejected'.", severity: "warning" });
            return;
        }
        handleOpenFormDialog(orderToResubmit);
    }, [user, userProfile, setSnackbar, handleOpenFormDialog]);

    return {
        formValues,
        setFormValues,
        openFormDialog,
        handleOpenFormDialog,
        handleCloseFormDialog,
        openItemDetailDialog,
        handleOpenItemDetailDialog,
        handleCloseItemDetailDialog,
        currentItemDetail,
        currentItemDetailIndex,
        handleItemDetailChange,
        handleSaveItemDetail,
        handleDeleteItemDetail,
        handleSubmitOrder,
        orderBeingEditedId,
        handleResubmitOrder,
        // Hapus handleMainFormChange dari return
    };
};

export default useOrderForm;
